import { useCallback, useSyncExternalStore } from 'react'

import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'

export type SeriesSelection = { seriesId: number; appointmentId: number; event: IPatientSchedule }

type Listener = () => void

// One Series card is selected at a time, across every calendar on the page.
let current: SeriesSelection | null = null
const listeners = new Set<Listener>()

const notify = (): void => listeners.forEach((listener) => listener())

export const subscribeSeriesSelection = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getSeriesSelection = (): SeriesSelection | null => current

export const selectSeries = (selection: SeriesSelection): void => {
  current = selection
  notify()
}

export const clearSeriesSelection = (): void => {
  if (current === null) return
  current = null
  notify()
}

// After a redraw, a selection whose card no calendar shows any more is dropped.
export const clearSeriesSelectionIfGone = (root: ParentNode): void => {
  if (current === null) return
  const card = root.querySelector(
    `[data-id="Appointment_${current.appointmentId}"]:not(.e-schedule-event-clone)`,
  )
  if (!card) clearSeriesSelection()
}

// With an appointment id, a card only re-renders when its own selection changes.
export const useSeriesSelection = (appointmentId?: number): SeriesSelection | null => {
  const read = useCallback(
    () =>
      appointmentId === undefined || current?.appointmentId === appointmentId ? current : null,
    [appointmentId],
  )
  return useSyncExternalStore(subscribeSeriesSelection, read, read)
}
